import { get, merge } from 'lodash';
import { ACClient as ACBaseClient } from 'vue-infra';
import Config from '@config/config';
import { RESOURCES_CONFIGURATION_TIMEOUT as TIMEOUT, SOURCE_CONTEXT_NAMES } from '@config/Consts';
import BaseClient from '@core/common/BaseClient';

const EVENTS = BaseClient.EVENTS;

const RESOURCES = {
  USERS: 'users',
  SKILLS: 'skills',
  AGENT_GROUPS: 'agentGroups',
  USER_MANAGED_DATA: 'userManagedData',
  STATUS_REASON: 'statusReasons',
  SITE_SETTINGS: 'siteSettings',
};

const RESOURCES_CONFIGURATION = {
  [RESOURCES.SKILLS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'AccountConfig.Skills',
    TIMEOUT: [TIMEOUT.AC],
  },
  [RESOURCES.USERS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'AccountConfig.Users',
    TIMEOUT: [TIMEOUT.AC],
  },
  [RESOURCES.AGENT_GROUPS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'AccountConfig.AgentGroups',
    TIMEOUT: [TIMEOUT.AC],
  },
  [RESOURCES.USER_MANAGED_DATA]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'AccountConfig.UserManagedData',
    TIMEOUT: [TIMEOUT.AC],
  },
  [RESOURCES.STATUS_REASON]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'AccountConfig.StatusReasons',
    TIMEOUT: [TIMEOUT.AC],
  },
  [RESOURCES.SITE_SETTINGS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET, BaseClient.HTTP_METHODS.PATCH],
    URL_KEY: () => 'AccountConfig.SiteSettings',
    TIMEOUT: [TIMEOUT.GENERAL_RESOURCE],
  },
};

function createACBaseClient(options, settings) {
  const { baseURL, apiResource } = BaseClient.urlResolver.resolveUrl(
    RESOURCES_CONFIGURATION[settings.resource],
    { userId: settings.loggedInUserId },
  );
  const params = { v: apiResource.version };
  // AC readwrite excepts path and not the full url
  const url = BaseClient.urlResolver.getUrlParts(baseURL);
  const urlSearchParam = get(settings, `extraResourceConfig.${settings.resource}.urlSearchParam`) || '';
  const urlSearch = url.search + urlSearchParam;
  const path = `${url.pathname}${urlSearch}${url.hash}`;

  const acBaseClientOptions = {
    allowedMethods: RESOURCES_CONFIGURATION[settings.resource].ALLOWED_METHODS,
    path,
    source: {
      appName: Config.moduleName,
      contextName: settings.contextName ?? SOURCE_CONTEXT_NAMES.ROOT, // TODO Omer find the reason why it fails in production
    },
    config: {
      params,
      timeout: RESOURCES_CONFIGURATION[settings.resource].TIMEOUT,
    },
    retryConfig: BaseClient.getClientRetryConfig,
  };
  merge(acBaseClientOptions, options);

  const acClient = new ACBaseClient(acBaseClientOptions);

  if (BaseClient.urlResolver.CONFIG.useMocks) {
    [acClient.readClient, acClient.writeClient].forEach((client) => {
      client.useRequestInterceptor((req) => {
        const request = req;
        request.url = baseURL;
        request.baseURL = baseURL;
        return request;
      });
    });
  }

  return acClient;
}

class ACClient extends BaseClient {
  constructor({
    infraBaseClientOptions,
    ...options
  }) {
    const infraBaseClient = createACBaseClient(
      infraBaseClientOptions, {
        resource: options.resource,
        loggedInUserId: options.loggedInUserId,
        extraResourceConfig: options.extraResourceConfig,
        contextName: options.contextName,
      });
    super({
      name: 'ACClient',
      infraBaseClient,
      ...options,
    });
  }
}

export {
  RESOURCES,
  EVENTS,
  ACClient,
};
